import type { SpeedJobResponse } from '@/lib/api/speed';

type ReceiptClient = {
    getTransactionReceipt(args: { hash: `0x${string}` }): Promise<{
        status: string;
        blockNumber: bigint;
    }>;
    readContract?(args: {
        address: `0x${string}`;
        abi: readonly unknown[];
        functionName: 'balanceOf';
        args: readonly [`0x${string}`];
    }): Promise<unknown>;
};

const UNOBSERVABLE_RECHECK_MS = 30_000;
const MAX_UNOBSERVABLE_RECHECK_MS = 5 * 60_000;
const OBSERVATION_INTERVAL_MS = 5_000;
const MAX_OBSERVATION_INTERVAL_MS = 30_000;
const BALANCE_OF_ABI = [{
    type: 'function',
    name: 'balanceOf',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }],
}] as const;

function transactionHash(value: unknown): `0x${string}` | null {
    return typeof value === 'string' && /^0x[0-9a-fA-F]{64}$/.test(value)
        ? (value.toLowerCase() as `0x${string}`)
        : null;
}

export function hyperEvmTransactionHashes(
    job: SpeedJobResponse,
): `0x${string}`[] {
    const response = job.response as Record<string, unknown> | null;
    if (!response) return [];
    const funding = response.hypercoreFunding as
        | Record<string, unknown>
        | null
        | undefined;
    const candidates = [
        response.exitSweepTxHash,
        response.coreWriterSendAssetTxHash,
        funding?.approveTxHash,
        funding?.fundTxHash,
    ];
    return [...new Set(candidates.map(transactionHash).filter(Boolean))] as `0x${string}`[];
}

function receiptMissing(error: unknown): boolean {
    if (!(error instanceof Error)) return false;
    return (
        error.name === 'TransactionReceiptNotFoundError' ||
        /receipt.+not found/i.test(error.message)
    );
}

export async function observeHyperEvmJob(
    job: SpeedJobResponse,
    client: ReceiptClient,
): Promise<{ fingerprint: string; hasObservableHashes: boolean }> {
    const hashes = hyperEvmTransactionHashes(job);
    const receipts = await Promise.all(
        hashes.map(async (hash) => {
            try {
                const receipt = await client.getTransactionReceipt({ hash });
                return `${hash}:${receipt.status}:${receipt.blockNumber}`;
            } catch (error) {
                if (receiptMissing(error)) return `${hash}:pending`;
                throw error;
            }
        }),
    );
    const response = job.response as Record<string, unknown> | null;
    const destinationCurrency = transactionAddress(response?.destinationCurrency);
    const destinationAddress = transactionAddress(response?.destinationAddress);
    let balanceFingerprint: string | null = null;
    if (job.kind === 'deposit'
        && job.status === 'bridging'
        && response?.destinationChainId === 999
        && destinationCurrency
        && destinationAddress
        && client.readContract) {
        const balance = await client.readContract({
            address: destinationCurrency,
            abi: BALANCE_OF_ABI,
            functionName: 'balanceOf',
            args: [destinationAddress],
        });
        balanceFingerprint = `balance:${destinationCurrency}:${destinationAddress}:${String(balance)}`;
    }
    const parts = balanceFingerprint ? [...receipts, balanceFingerprint] : receipts;
    return {
        fingerprint: parts.length === 0 ? 'no-observable-hash' : parts.join('|'),
        hasObservableHashes: hashes.length > 0 || balanceFingerprint !== null,
    };
}

function transactionAddress(value: unknown): `0x${string}` | null {
    return typeof value === 'string' && /^0x[0-9a-fA-F]{40}$/.test(value)
        ? (value.toLowerCase() as `0x${string}`)
        : null;
}

export function nextObservationDelayMs(unchangedSnapshots: number): number {
    return Math.min(
        OBSERVATION_INTERVAL_MS * (2 ** Math.max(0, unchangedSnapshots)),
        MAX_OBSERVATION_INTERVAL_MS,
    );
}

export function shouldRequestJobRecheck(input: {
    previousFingerprint: string | undefined;
    fingerprint: string;
    force: boolean;
    hasObservableHashes: boolean;
    lastRecheckAt: number | undefined;
    now: number;
    unchangedRechecks?: number;
}): boolean {
    if (input.force || input.previousFingerprint === undefined) return true;
    if (input.previousFingerprint !== input.fingerprint) return true;
    return (
        !input.hasObservableHashes &&
        input.now - (input.lastRecheckAt ?? 0) >= Math.min(
            UNOBSERVABLE_RECHECK_MS * (2 ** Math.max(0, input.unchangedRechecks ?? 0)),
            MAX_UNOBSERVABLE_RECHECK_MS,
        )
    );
}
